import { ALL_LANGUAGE_OPTIONS } from "@/lib/utils/default-returner";
import { langCandidates } from "@/lib/utils/resolve-lang";

/**
 * Canonical full-locale standardization for template languages.
 *
 * Templates historically stored short / generic language codes ("de", "en")
 * from the legacy `htmlLanguages` list, while guests store full BCP-47 locales
 * ("de-DE", "en-GB") from `ALL_LANGUAGE_OPTIONS`. We standardize templates on
 * the same full-locale list. Because there is no clean 1:1 mapping (a bare
 * "de" could be de-DE / de-AT / de-CH), the base→full choice is OPINIONATED
 * and lives here.
 *
 * Kept deliberately small and explicit so the choice is auditable.
 */

const ALL_SET = new Set(ALL_LANGUAGE_OPTIONS);

/**
 * Opinionated base-language → canonical full locale. Keyed by the 2/3-letter
 * base; every value MUST exist in ALL_LANGUAGE_OPTIONS (asserted by the test).
 * Used both for legacy data migration and for normalizing dirty inputs from
 * the public-registration / CSV-import write paths.
 */
export const LEGACY_LANG_MAP: Record<string, string> = {
  af: "af-ZA", sq: "sq-AL", am: "am-ET", ar: "ar-SA", hy: "hy-AM",
  az: "az-AZ", eu: "eu-ES", be: "be-BY", bn: "bn-BD", bs: "bs-BA",
  bg: "bg-BG", my: "my-MM", ca: "ca-ES", zh: "zh-CN", hr: "hr-HR",
  cs: "cs-CZ", da: "da-DK", nl: "nl-NL", en: "en-GB", et: "et-EE",
  fi: "fi-FI", fr: "fr-FR", ka: "ka-GE", de: "de-DE", el: "el-GR",
  gu: "gu-IN", ha: "ha-NG", he: "he-IL", hi: "hi-IN", hu: "hu-HU",
  is: "is-IS", id: "id-ID", ga: "ga-IE", it: "it-IT", ja: "ja-JP",
  kn: "kn-IN", kk: "kk-KZ", km: "km-KH", ko: "ko-KR", ky: "ky-KG",
  lo: "lo-LA", lv: "lv-LV", lt: "lt-LT", lb: "lb-LU", mk: "mk-MK",
  ms: "ms-MY", ml: "ml-IN", mt: "mt-MT", mr: "mr-IN", mn: "mn-MN",
  ne: "ne-NP", nb: "nb-NO", no: "nb-NO", fa: "fa-IR", pl: "pl-PL",
  pt: "pt-PT", pa: "pa-IN", ro: "ro-RO", ru: "ru-RU", sr: "sr-RS",
  si: "si-LK", sk: "sk-SK", sl: "sl-SI", so: "so-SO", es: "es-ES",
  sw: "sw-KE", sv: "sv-SE", tl: "tl-PH", ta: "ta-IN", te: "te-IN",
  th: "th-TH", tr: "tr-TR", uk: "uk-UA", ur: "ur-PK", uz: "uz-UZ",
  vi: "vi-VN", cy: "cy-GB", yo: "yo-NG", zu: "zu-ZA",
};

/**
 * Normalize an arbitrary language code to a canonical full locale from
 * ALL_LANGUAGE_OPTIONS, or return undefined if it cannot be resolved.
 *
 *   "de-DE" → "de-DE"   (already canonical)
 *   "de"    → "de-DE"   (opinionated base map)
 *   "de-AT" → "de-DE"   (regional variant not in the list → base map)
 *   "DE"    → "de-DE"   (case-insensitive)
 *   "German"→ undefined (free text — caller decides fallback)
 */
export function normalizeToFullLocale(
  code: string | null | undefined,
): string | undefined {
  if (typeof code !== "string") return undefined;
  const c = code.trim();
  if (c.length === 0) return undefined;

  // Exact (case-insensitive) hit against the canonical list.
  if (ALL_SET.has(c)) return c;
  const exactCi = ALL_LANGUAGE_OPTIONS.find((l) => l.toLowerCase() === c.toLowerCase());
  if (exactCi !== undefined) return exactCi;

  const base = c.toLowerCase().split("-")[0]!;
  const mapped = LEGACY_LANG_MAP[base];
  if (mapped !== undefined && ALL_SET.has(mapped)) return mapped;

  // Last resort: any canonical locale sharing the base language.
  const byBase = ALL_LANGUAGE_OPTIONS.find((l) => l.toLowerCase().startsWith(`${base}-`));
  return byBase;
}

/**
 * Rewrite the keys of a per-language map to canonical full locales. Keys that
 * cannot be normalized are kept as-is. On collision the first writer wins so a
 * pre-existing canonical key is never clobbered by a normalized legacy one.
 */
export function normalizeMapKeys(
  map: Record<string, string> | undefined | null,
): Record<string, string> {
  if (map === undefined || map === null) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(map)) {
    const full = normalizeToFullLocale(key) ?? key;
    if (out[full] === undefined) out[full] = value;
  }
  return out;
}

// Re-export so callers can share the candidate chain without a second import.
export { langCandidates };
